import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ApyConfig {
  mode: 'auto' | 'manual';
  manual_value: number;
  auto_min: number;
  auto_max: number;
}

export interface StakingConfig {
  usdt_enabled: boolean;
  trx_enabled: boolean;
  usdt_apy: ApyConfig;
  trx_apy: ApyConfig;
}

const DEFAULT_CONFIG: StakingConfig = {
  usdt_enabled: true,
  trx_enabled: true,
  usdt_apy: { mode: 'auto', manual_value: 24, auto_min: 21, auto_max: 27 },
  trx_apy: { mode: 'auto', manual_value: 6, auto_min: 5, auto_max: 7 }
};

// Seeded random generator for consistent daily values
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

export const useStakingConfig = () => {
  const [config, setConfig] = useState<StakingConfig>(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await supabase.functions.invoke('admin', {
          body: { action: 'get_staking_config' }
        });

        if (response.data?.success && response.data?.config) {
          // Merge with defaults to ensure all fields exist
          setConfig({
            ...DEFAULT_CONFIG,
            ...response.data.config,
            usdt_apy: { ...DEFAULT_CONFIG.usdt_apy, ...response.data.config?.usdt_apy },
            trx_apy: { ...DEFAULT_CONFIG.trx_apy, ...response.data.config?.trx_apy }
          });
        }
      } catch (error) {
        console.error('Failed to fetch staking config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Calculate effective APY based on mode
  const effectiveApy = useMemo(() => {
    const today = new Date();
    const daySeed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
    
    const calculateApy = (apyConfig: ApyConfig): number => {
      if (apyConfig.mode === 'manual') {
        return apyConfig.manual_value;
      }
      // Auto mode: fluctuate within range
      const range = apyConfig.auto_max - apyConfig.auto_min;
      const randomFactor = seededRandom(daySeed);
      return apyConfig.auto_min + (range * randomFactor);
    };

    return {
      usdt: parseFloat(calculateApy(config.usdt_apy).toFixed(1)),
      trx: parseFloat(calculateApy(config.trx_apy).toFixed(1))
    };
  }, [config]);

  const isStakingEnabled = config.usdt_enabled || config.trx_enabled;

  return { config, isLoading, isStakingEnabled, effectiveApy };
};
